import React, { useContext, useState } from "react";
import { Pivot, PivotItem } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import themeContext from "../../../../../context/theme/ThemeContext";
import "../../../styles/ColorLUTTool.css";

/**
 * Modal body for the Fusion tool.
 * Two tabs: "PET Series" (filtered to PT/NM/PET modalities) and "All Series".
 * Clicking a series card triggers fusion on the active viewport.
 */
const ColorLUTBody = ({ availableSeries, onSeriesSelect, selectedSeriesId }) => {
  const { t } = useTranslation();
  const { themeName } = useContext(themeContext);
  const [activeTab, setActiveTab] = useState("pet");

  // Filter PET/NM series — use the raw per-series DICOM modality (seriesModality),
  // not the study-level modality, so a combined study (e.g. PET/CT) doesn't list
  // its CT series under "PET Series".
  const petSeries = (availableSeries || []).filter((s) => {
    const mod = (s.seriesModality || "").toUpperCase();
    return mod === "PT" || mod === "NM" || mod === "PET";
  });

  const renderSeriesList = (seriesList) => {
    if (!seriesList || seriesList.length === 0) {
      return (
        <div className="fusion-no-series">
          {t("viewer.tools.fusion-no-series", "No series found")}
        </div>
      );
    }

    return (
      <div className="fusion-series-list">
        {seriesList.map((series, idx) => {
          const isSelected = selectedSeriesId === series.seriesId;
          const mod = (series.seriesModality || "").toUpperCase();
          const desc =
            series.seriesDescription ||
            t("viewer.tools.fusion-series-fallback", "Series {{id}}", { id: series.stackId || idx + 1 });
          const imageCount = series.imageIds ? series.imageIds.length : 0;

          return (
            <div
              key={series.seriesId || idx}
              className={`fusion-series-card ${isSelected ? "selected" : ""}`}
              onClick={() => onSeriesSelect(series)}
              title={desc}
            >
              <div className="fusion-series-card-left">
                <span className="fusion-series-modality">{mod}</span>
              </div>
              <div className="fusion-series-card-right">
                <span className="fusion-series-desc">{desc}</span>
                <span className="fusion-series-meta">
                  {t("viewer.tools.fusion-image-count", "{{count}} image", { count: imageCount })}{imageCount !== 1 ? "s" : ""}
                  {series.bodyPart ? ` · ${series.bodyPart}` : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="color-lut-modal-body" data-theme={themeName}>
      <Pivot
        className="fusion-tabs-pivot"
        aria-label={t("viewer.tools.fusion-series-tabs", "Series tabs")}
        linkSize="normal"
        linkFormat="tabs"
        selectedKey={activeTab}
        onLinkClick={(item) => setActiveTab(item.props.itemKey)}
      >
        <PivotItem
          headerText={`${t("viewer.tools.fusion-pet-series", "PET Series")} (${petSeries.length})`}
          itemKey="pet"
        >
          <div className="fusion-tab-content">
            {renderSeriesList(petSeries)}
          </div>
        </PivotItem>
        <PivotItem
          headerText={`${t("viewer.tools.fusion-all-series", "All Series")} (${(availableSeries || []).length})`}
          itemKey="all"
        >
          <div className="fusion-tab-content">
            {renderSeriesList(availableSeries || [])}
          </div>
        </PivotItem>
      </Pivot>
    </div>
  );
};

export default ColorLUTBody;
